import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUp } from "lucide-react";
import { api } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import { readCache, writeCache } from "@/lib/cache";
import SmartImage from "@/components/SmartImage";
import ImageSkeleton from "@/components/ImageSkeleton";

const CATEGORIES = [
  { id: "all", label: "Tout" },
  { id: "jardins-prives", label: "Jardins privés" },
  { id: "entretien", label: "Entretien" },
  { id: "plantations", label: "Plantations" },
  { id: "amenagements", label: "Aménagements" },
];

export default function RealisationsPage() {
  const [items, setItems] = useState(() => readCache("realizations", []));
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [showBackTop, setShowBackTop] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    api.get("/realizations?summary=true").then((r) => {
      const data = r.data || [];
      setItems(data);
      writeCache("realizations", data);
      setLoaded(true);
    });
  }, []);

  // Quand on clique sur une réalisation, on charge la version complète (avec galerie + vidéos)
  const openRealisation = async (r) => {
    setActive({ ...r, _loading: true });
    try {
      const { data } = await api.get(`/realizations/${r.id}`);
      setActive(data);
    } catch (e) {
      setActive(r); // fallback sur les données light
    }
  };

  useEffect(() => {
    document.body.style.overflow = active ? "hidden" : "";
    setShowBackTop(false);
    if (modalRef.current) modalRef.current.scrollTop = 0;
    return () => { document.body.style.overflow = ""; };
  }, [active]);

  const onModalScroll = (e) => {
    setShowBackTop(e.currentTarget.scrollTop > 400);
  };

  const scrollTop = () => {
    if (modalRef.current) modalRef.current.scrollTo({ top: 0, behavior: "smooth" });
  };

  const visible = filter === "all" ? items : items.filter((r) => r.category === filter);

  return (
    <PublicLayout>
      <section className="pt-36 pb-12 container-px max-w-7xl mx-auto">
        <p className="label-eyebrow mb-5">Réalisations</p>
        <h1 className="font-display text-5xl md:text-7xl text-forest font-light tracking-tight max-w-3xl leading-[0.95]">
          Nos jardins, en images.
        </h1>
      </section>

      <section className="container-px max-w-7xl mx-auto pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {!loaded
            ? Array.from({ length: 6 }).map((_, i) => (
                <ImageSkeleton key={i} className={i % 5 === 0 ? "md:col-span-2 aspect-[16/10]" : "aspect-[4/5]"} />
              ))
            : visible.map((r, i) => (
            <motion.button
              key={r.id}
              layout
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: (i % 6) * 0.04 }}
              onClick={() => openRealisation(r)}
              data-testid={`realisation-card-${i}`}
              className={`group relative overflow-hidden text-left ${
                i % 5 === 0 ? "md:col-span-2 aspect-[16/10]" : "aspect-[4/5]"
              }`}
            >
              <img
                src={r.cover}
                alt={r.title}
                className="w-full h-full object-cover group-hover:scale-110 transition duration-1000"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-forest/85 via-forest/10 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6 text-cream">
                <p className="text-[10px] uppercase tracking-[0.22em] opacity-80">
                  {CATEGORIES.find((c) => c.id === r.category)?.label}
                </p>
                <h3 className="font-display text-2xl mt-2">{r.title}</h3>
              </div>
            </motion.button>
          ))}
          {visible.length === 0 && (
            <p className="col-span-full text-forest-soft text-center py-16">Aucune réalisation pour cette catégorie.</p>
          )}
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            ref={modalRef}
            onScroll={onModalScroll}
            className="fixed inset-0 z-[60] bg-forest/95 backdrop-blur-md overflow-y-auto"
            data-testid="realisation-modal"
          >
            <button
              onClick={() => setActive(null)}
              className="fixed top-6 right-6 z-10 w-11 h-11 flex items-center justify-center bg-cream text-forest hover:bg-corten hover:text-cream transition shadow-lg"
              aria-label="Fermer"
              data-testid="modal-close"
            >
              <X className="w-5 h-5" />
            </button>

            <AnimatePresence>
              {showBackTop && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  onClick={(e) => { e.stopPropagation(); scrollTop(); }}
                  className="fixed bottom-6 right-6 z-10 w-12 h-12 flex items-center justify-center bg-corten text-cream hover:bg-corten-dark transition shadow-xl rounded-full"
                  aria-label="Retour en haut"
                  data-testid="modal-back-top"
                >
                  <ArrowUp className="w-5 h-5" />
                </motion.button>
              )}
            </AnimatePresence>

            <motion.article
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="min-h-screen"
            >
              {/* Cover plein écran */}
              <header className="relative h-[70vh] min-h-[500px] flex items-end overflow-hidden">
                {active.cover && (
                  <SmartImage
                    src={active.cover}
                    alt={active.title}
                    className="absolute inset-0 w-full h-full"
                    width={1920}
                    eager
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-forest/95 via-forest/40 to-transparent" />
                <div className="relative container-px max-w-5xl mx-auto pb-16 text-cream">
                  <p className="label-eyebrow text-cream/70 mb-5">
                    {CATEGORIES.find((c) => c.id === active.category)?.label}
                  </p>
                  <h2 className="font-display text-5xl md:text-6xl lg:text-7xl font-light leading-[0.95] tracking-tight max-w-3xl">
                    {active.title}
                  </h2>
                  {active.description && (
                    <p className="mt-8 max-w-2xl text-cream/85 text-base md:text-lg leading-relaxed font-light">
                      {active.description}
                    </p>
                  )}
                </div>
              </header>

              {/* Galerie élégante */}
              <div className="bg-cream py-20 md:py-28">
                <div className="container-px max-w-5xl mx-auto">
                  {(active.images || []).length === 0 && (
                    <p className="text-center text-forest-soft italic">Aucune photo supplémentaire pour cette réalisation.</p>
                  )}
                  <div className="space-y-20 md:space-y-28">
                    {(active.images || []).map((raw, k) => {
                      const src = typeof raw === "string" ? raw : raw.src;
                      const caption = typeof raw === "string" ? "" : (raw.caption || "");
                      const isOdd = k % 2 === 1;
                      return (
                        <motion.figure
                          key={k}
                          initial={{ opacity: 0, y: 30 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true, margin: "-80px" }}
                          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                          className={`grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center ${isOdd ? "md:[&>img]:order-2" : ""}`}
                        >
                          <SmartImage
                            src={src}
                            alt={caption || `${active.title} — image ${k + 1}`}
                            className="md:col-span-8 w-full aspect-[4/3]"
                            width={1400}
                          />
                          <figcaption className="md:col-span-4 md:px-2">
                            <span className="label-eyebrow block mb-3">
                              {String(k + 1).padStart(2, "0")} / {String(active.images.length).padStart(2, "0")}
                            </span>
                            {caption ? (
                              <p className="font-display text-2xl md:text-3xl text-forest font-light italic leading-snug tracking-tight">
                                « {caption} »
                              </p>
                            ) : (
                              <p className="text-sm text-forest-soft italic">Image {k + 1}</p>
                            )}
                            <div className="mt-5 w-12 h-px bg-corten" />
                          </figcaption>
                        </motion.figure>
                      );
                    })}
                  </div>

                  {/* Vidéos */}
                  {active.videos && active.videos.length > 0 && (
                    <div className="mt-20 space-y-8">
                      <p className="label-eyebrow text-center">Vidéos</p>
                      {active.videos.map((v, k) => (
                        <video key={k} src={v} controls className="w-full" />
                      ))}
                    </div>
                  )}

                  {/* CTA bas de modal */}
                  <div className="mt-24 pt-12 border-t border-sand text-center">
                    <p className="font-display text-3xl md:text-4xl text-forest font-light italic">
                      Un projet similaire vous inspire&nbsp;?
                    </p>
                    <a href="/contact" className="btn-corten mt-8">
                      Demander un devis
                    </a>
                  </div>
                </div>
              </div>
            </motion.article>
          </motion.div>
        )}
      </AnimatePresence>
    </PublicLayout>
  );
}
